export interface RequestPreparation {
  signal?: AbortSignal;
  assertContextCurrent?: () => void;
}

function yieldRequestPreparation(signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const finish = () => {
      signal?.removeEventListener('abort', abort);
      resolve();
    };
    const timer = setTimeout(finish, 0);
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      reject(signal?.reason);
    };
    signal?.addEventListener('abort', abort, { once: true });
  });
}

/** Native escaping on small fragments, keeping surrogate pairs together. */
function* quote(value: string): Generator<string> {
  yield '"';
  for (let start = 0; start < value.length; ) {
    let end = Math.min(start + 2048, value.length);
    const high = value.charCodeAt(end - 1);
    const low = value.charCodeAt(end);
    if (high >= 0xd800 && high <= 0xdbff && low >= 0xdc00 && low <= 0xdfff)
      end++;
    yield JSON.stringify(value.slice(start, end)).slice(1, -1);
    start = end;
  }
  yield '"';
}

function jsonValue(value: unknown, key: string): unknown {
  if (
    value !== null &&
    (typeof value === 'object' ||
      typeof value === 'function' ||
      typeof value === 'bigint')
  ) {
    const toJSON = (value as { toJSON?: unknown }).toJSON;
    if (typeof toJSON === 'function') value = toJSON.call(value, key);
  }
  if (value !== null && typeof value === 'object') {
    // Normal wire records need no probing or copying. Rare boxed primitives
    // retain JSON's conversion, including wrappers from another realm.
    const prototype: unknown = Object.getPrototypeOf(value);
    if (
      prototype !== null &&
      prototype !== Object.prototype &&
      !Array.isArray(value)
    ) {
      for (const unbox of [
        Number.prototype.valueOf,
        String.prototype.valueOf,
        Boolean.prototype.valueOf,
        BigInt.prototype.valueOf,
      ]) {
        let primitive: unknown;
        try {
          primitive = Reflect.apply(unbox, value, []);
        } catch {
          continue;
        }
        if (typeof primitive === 'number') return Number(value);
        if (typeof primitive === 'string') return String(value);
        return primitive;
      }
    }
  }
  return value;
}

interface Frame {
  value: object;
  array: boolean;
  keys: string[];
  length: number;
  index: number;
  first: boolean;
}

/** The request's single traversal; no recursive tree or upfront deep clone. */
function* jsonParts(input: unknown): Generator<string, boolean> {
  const ancestors = new WeakSet<object>();
  const stack: Frame[] = [];
  let value = jsonValue(input, '');
  if (
    value === undefined ||
    typeof value === 'function' ||
    typeof value === 'symbol'
  )
    return false;
  while (true) {
    if (typeof value === 'string') yield* quote(value);
    else if (value === null || typeof value !== 'object') {
      // Native number/boolean/null and BigInt error behavior.
      yield JSON.stringify(value);
    } else {
      if (ancestors.has(value))
        throw new TypeError('Converting circular structure to JSON');
      ancestors.add(value);
      const array = Array.isArray(value);
      const keys = array ? [] : Object.keys(value);
      stack.push({
        value,
        array,
        keys,
        length: Array.isArray(value) ? value.length : keys.length,
        index: 0,
        first: true,
      });
      yield array ? '[' : '{';
    }

    let next = false;
    while (stack.length) {
      const frame = stack[stack.length - 1];
      if (frame.index >= frame.length) {
        yield frame.array ? ']' : '}';
        ancestors.delete(frame.value);
        stack.pop();
        continue;
      }
      const key = frame.array ? String(frame.index) : frame.keys[frame.index];
      frame.index++;
      value = jsonValue((frame.value as Record<string, unknown>)[key], key);
      if (
        value === undefined ||
        typeof value === 'function' ||
        typeof value === 'symbol'
      ) {
        if (!frame.array) {
          // Skipped keys consume work too; a huge omitted tail must yield.
          yield '';
          continue;
        }
        value = null;
      }
      if (!frame.first) yield ',';
      frame.first = false;
      if (!frame.array) {
        yield* quote(key);
        yield ':';
      }
      next = true;
      break;
    }
    if (!next) return true;
  }
}

/** Bound traversal/escaping before returning the exact JSON wire string. */
export async function prepareRequestJSON(
  value: unknown,
  { signal, assertContextCurrent }: RequestPreparation = {}
): Promise<string | undefined> {
  const check = () => {
    signal?.throwIfAborted();
    assertContextCurrent?.();
  };
  check();
  const work = jsonParts(value);
  const chunks: string[] = [];
  let parts: string[] = [];
  let characters = 0;
  const flush = () => {
    if (!parts.length) return;
    chunks.push(parts.join(''));
    parts = [];
    characters = 0;
  };
  let steps = 0;
  let deadline = performance.now() + 4;
  try {
    while (true) {
      signal?.throwIfAborted();
      const next = work.next();
      if (next.done) {
        check();
        flush();
        return next.value ? chunks.join('') : undefined;
      }
      parts.push(next.value);
      characters += next.value.length;
      // Release tiny field/escape fragments instead of retaining the full
      // number of properties until final assembly. Each coalescing is bounded.
      if (characters >= 32768 || parts.length >= 1024) flush();
      if (++steps >= 256 || performance.now() >= deadline) {
        check();
        await yieldRequestPreparation(signal);
        check();
        steps = 0;
        deadline = performance.now() + 4;
      }
    }
  } finally {
    work.return(false);
  }
}
